import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, Pencil, Compass, ShieldCheck, MailWarning, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/MobileShell";
import { Button, Card, Spinner } from "@/components/kit";
import { AuthImage } from "@/components/AuthImage";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/profile"), api.get("/photos"), api.get("/prompt-answers"), api.get("/reference"),
    ]).then(([p, ph, a, r]) => {
      setProfile(p.data); setPhotos(ph.data); setAnswers(a.data); setPrompts(r.data.prompts);
    });
  }, []);

  const resendVerification = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent."); }
    catch { toast.error("Could not send email right now."); }
  };

  if (!profile) return <><Header title="Profile" /><div className="flex justify-center py-20"><Spinner /></div></>;

  const promptText = (pid) => prompts.find((p) => p.id === pid)?.text || "";

  return (
    <>
      <Header
        title="Profile"
        right={<button onClick={() => navigate("/app/settings")} className="p-1.5 text-[#5F6D66]" data-testid="profile-settings-btn"><Settings className="h-5 w-5" strokeWidth={1.7} /></button>}
      />
      <div className="px-6 py-5 space-y-5">
        {/* header card */}
        <Card className="overflow-hidden animate-rise" data-testid="profile-card">
          <div className="aspect-[4/5] bg-[#F1EEE7] relative">
            {photos[0] ? <AuthImage photoId={(photos.find(p=>p.is_primary)||photos[0]).id} className="h-full w-full object-cover" />
              : <div className="h-full w-full flex items-center justify-center text-[#B2BAB4]">No photo</div>}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1F2B26]/80 to-transparent p-4">
              <h2 className="font-serif text-2xl text-white" data-testid="profile-name">
                {profile.display_name}{user?.age ? <span className="font-sans font-light">, {user.age}</span> : null}
              </h2>
              <p className="font-sans text-sm text-white/85">{profile.city}</p>
            </div>
          </div>
        </Card>

        {/* status row */}
        <div className="flex gap-3">
          <div className="flex-1 rounded-2xl bg-white border border-[#E3DCD0] p-3.5 flex items-center gap-2.5" data-testid="profile-mode-status">
            <Compass className="h-5 w-5 text-[#253B35]" strokeWidth={1.6} />
            <div>
              <p className="font-sans text-[11px] text-[#8A9790] uppercase tracking-wide">Mode</p>
              <p className="font-sans text-sm font-medium text-[#1F2B26]">Explore</p>
            </div>
          </div>
          <div className="flex-1 rounded-2xl bg-white border border-[#E3DCD0] p-3.5 flex items-center gap-2.5" data-testid="profile-standing">
            <ShieldCheck className="h-5 w-5 text-[#3D6B56]" strokeWidth={1.6} />
            <div>
              <p className="font-sans text-[11px] text-[#8A9790] uppercase tracking-wide">Standing</p>
              <p className="font-sans text-sm font-medium text-[#1F2B26]">Good</p>
            </div>
          </div>
        </div>

        {/* email verification nudge */}
        {!user?.email_verified ? (
          <div className="rounded-2xl bg-[#F3EBE1] border border-[#E3DCD0] p-4 flex items-start gap-3" data-testid="profile-verify-nudge">
            <MailWarning className="h-5 w-5 text-[#B88E4C] mt-0.5 shrink-0" strokeWidth={1.7} />
            <div className="flex-1">
              <p className="font-sans text-sm text-[#1F2B26] font-medium">Verify your email</p>
              <p className="font-sans text-xs text-[#5F6D66] mt-0.5">Confirm your email to unlock Focus mode later.</p>
              <button onClick={resendVerification} className="font-sans text-xs text-[#253B35] underline underline-offset-2 mt-1.5" data-testid="profile-resend-verification-btn">Resend verification email</button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-[#E3DCD0] p-3.5 flex items-center gap-2.5" data-testid="profile-verified-badge">
            <CheckCircle2 className="h-5 w-5 text-[#3D6B56]" strokeWidth={1.7} />
            <p className="font-sans text-sm text-[#1F2B26]">Email verified</p>
          </div>
        )}

        {/* intent + bio */}
        <Card className="p-5">
          <span className="inline-block rounded-full bg-[#F1EEE7] px-3 py-1 font-sans text-xs text-[#5F6D66]">{profile.relationship_intention}</span>
          <p className="font-sans text-sm text-[#5F6D66] leading-relaxed mt-3">{profile.bio}</p>
          {profile.interests?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {profile.interests.map((i) => <span key={i} className="rounded-full border border-[#E3DCD0] px-3 py-1 font-sans text-xs text-[#5F6D66]">{i}</span>)}
            </div>
          )}
        </Card>

        {/* prompts */}
        {answers.map((a) => (
          <Card key={a.id} className="p-5">
            <p className="font-serif italic text-lg text-[#253B35]">{promptText(a.prompt_id)}</p>
            <p className="font-sans text-[15px] text-[#1F2B26] mt-1.5">{a.answer}</p>
          </Card>
        ))}

        <Button variant="outline" className="w-full" size="lg" onClick={() => navigate("/onboarding?edit=1")} data-testid="profile-edit-btn">
          <Pencil className="h-4 w-4" /> Edit profile
        </Button>
      </div>
    </>
  );
}
